var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Exclude, Expose, Type } from 'class-transformer';
import { Role } from '../../roles/index.js';
import { AddressFieldDto } from '../address-field.js';
import { AccessStatus } from '../enums/access-status.enum.js';
import { CaregiverRelationship } from '../enums/caregiver.enum.js';
import { TrainingReminderTime } from '../enums/training-reminder-time.enum.js';
/**
 * Response DTO for User data returned from queries.
 * Replaces usage of the legacy User entity and UserFullSizeDto in query return types.
 * Contains only identity-owned fields — cross-context data lives in
 * context-owned read models (office-mgm, therapy, etc.).
 *
 * Uses @Expose() / @Exclude() with role-based groups
 * for serialization filtering (matches legacy UserFullSizeDto visibility rules).
 */
export class UserResponseDto {
    id;
    createdAt;
    updatedAt;
    deletedAt;
    // Identity
    keycloakId;
    /** The Login address while an Account stands behind the Person. */
    email;
    /**
     * The Contact address: where the office sends this Person's mail (#1849). The
     * lookup's answer, not a column — for a patient without a login it is the
     * address the office recorded, otherwise the login address. Optional only for
     * the DTOs built without the mapper (the auth user, test doubles).
     */
    contactEmail;
    /**
     * The email field as this viewer may see and change it. Only `GET /users/:id`
     * fills it, since it depends on who asks; absent everywhere else.
     */
    addressField;
    /** Login lifecycle (Zugangsstatus, TM-9118) — drives the patient access badge. */
    accessStatus;
    firstName;
    lastName;
    fullName;
    // Role & status
    role;
    isOnboarded;
    isArchived;
    // Office
    officeId;
    currentOfficeLocationId;
    // Therapist assignment
    therapistId;
    // Profile
    birthdate;
    mobilePhone;
    profileImage;
    registeredAt;
    street;
    zip;
    town;
    industry;
    website;
    krankenkasse;
    pkvInsurerName;
    pkvPolicyNumber;
    salutation;
    countryCode;
    notes;
    therapyArea;
    lastVersion;
    // Consent & terms
    agreedTermsConditions;
    dateAgreedTermsConditions;
    agreedTermsConditionsType;
    agreedConsentfile;
    agreedNewsletter;
    // ── Identity-owned relations (populated depending on query) ───────────
    allowedOfficeLocations;
    allowedOfficeLocationIds;
    therapist;
    office;
    currentOfficeLocation;
    patients;
    userSettings;
    patientStatus;
    consentDeclaration;
    /**
     * The caregiver entry that declared the patient's consent (#1843), as the
     * file shows it: „erklärt durch Anna Muster, Elternteil“. Null when the
     * patient declared it or the office recorded it; undefined when not loaded.
     */
    consentDeclaredBy;
    index;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Date)
], UserResponseDto.prototype, "createdAt", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Date)
], UserResponseDto.prototype, "updatedAt", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "deletedAt", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "keycloakId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "email", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "contactEmail", void 0);
__decorate([
    Expose(),
    Type(() => AddressFieldDto),
    __metadata("design:type", AddressFieldDto)
], UserResponseDto.prototype, "addressField", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseDto.prototype, "accessStatus", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "firstName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "lastName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseDto.prototype, "fullName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseDto.prototype, "role", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseDto.prototype, "isOnboarded", void 0);
__decorate([
    Expose({ groups: [Role.SUPERADMIN, Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT] }),
    __metadata("design:type", Boolean)
], UserResponseDto.prototype, "isArchived", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "officeId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "currentOfficeLocationId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "therapistId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "birthdate", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "mobilePhone", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "profileImage", void 0);
__decorate([
    Expose({ groups: [Role.SUPERADMIN, Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT] }),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "registeredAt", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "street", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "zip", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "town", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "industry", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "website", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "krankenkasse", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "pkvInsurerName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "pkvPolicyNumber", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "salutation", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "countryCode", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "notes", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "therapyArea", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "lastVersion", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Boolean)
], UserResponseDto.prototype, "agreedTermsConditions", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "dateAgreedTermsConditions", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "agreedTermsConditionsType", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Boolean)
], UserResponseDto.prototype, "agreedConsentfile", void 0);
__decorate([
    Expose({ groups: [Role.SUPERADMIN] }),
    __metadata("design:type", Boolean)
], UserResponseDto.prototype, "agreedNewsletter", void 0);
__decorate([
    Expose({ groups: [Role.SUPERADMIN, Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT] }),
    Type(() => UserResponseOfficeLocationDto),
    __metadata("design:type", Array)
], UserResponseDto.prototype, "allowedOfficeLocations", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], UserResponseDto.prototype, "allowedOfficeLocationIds", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "therapist", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "office", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "currentOfficeLocation", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Array)
], UserResponseDto.prototype, "patients", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "userSettings", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "patientStatus", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "consentDeclaration", void 0);
__decorate([
    Expose(),
    Type(() => ConsentDeclarerDto),
    __metadata("design:type", Object)
], UserResponseDto.prototype, "consentDeclaredBy", void 0);
__decorate([
    Exclude(),
    __metadata("design:type", Number)
], UserResponseDto.prototype, "index", void 0);
/**
 * Lightweight nested DTO for office location references within UserResponseDto.
 */
export class UserResponseOfficeLocationDto {
    id;
    name;
    abbreviation;
    officeId;
    ownerId;
    /**
     * Calendar grid interval in minutes. Rides on the auth payload so the
     * calendar can apply it on load without a separate fetch.
     */
    calendarInterval;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseOfficeLocationDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseOfficeLocationDto.prototype, "name", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseOfficeLocationDto.prototype, "abbreviation", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseOfficeLocationDto.prototype, "officeId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseOfficeLocationDto.prototype, "ownerId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], UserResponseOfficeLocationDto.prototype, "calendarInterval", void 0);
/**
 * Lightweight nested DTO for office references within UserResponseDto.
 */
export class UserResponseOfficeDto {
    id;
    enabledFeatureFlags;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseOfficeDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], UserResponseOfficeDto.prototype, "enabledFeatureFlags", void 0);
/**
 * Lightweight nested DTO for user settings within UserResponseDto.
 */
export class UserResponseUserSettingsDto {
    id;
    userId;
    systemNotificationRegistrationEnabled;
    systemNotificationShopEnabled;
    systemNotificationPatientHandOverEnabled;
    systemNotificationAppointmentEnabled;
    systemNotificationExerciseProgramEnabled;
    systemNotificationTrainingReminderEnabled;
    systemNotificationProgramStatsEnabled;
    systemNotificationInvoicesEnabled;
    emailNotificationRegistrationEnabled;
    emailNotificationShopEnabled;
    emailNotificationPatientHandOverEnabled;
    emailNotificationAppointmentEnabled;
    emailNotificationExerciseProgramEnabled;
    emailNotificationTrainingReminderEnabled;
    emailNotificationProgramStatsEnabled;
    emailNotificationInvoicesEnabled;
    pushNotificationRegistrationEnabled;
    pushNotificationShopEnabled;
    pushNotificationPatientHandOverEnabled;
    pushNotificationAppointmentEnabled;
    pushNotificationExerciseProgramEnabled;
    pushNotificationTrainingReminderEnabled;
    pushNotificationProgramStatsEnabled;
    pushNotificationInvoicesEnabled;
    trainingReminderTime;
    defaultVideoCallDuration;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseUserSettingsDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseUserSettingsDto.prototype, "userId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationRegistrationEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationShopEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationPatientHandOverEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationAppointmentEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationExerciseProgramEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationTrainingReminderEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationProgramStatsEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "systemNotificationInvoicesEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationRegistrationEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationShopEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationPatientHandOverEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationAppointmentEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationExerciseProgramEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationTrainingReminderEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationProgramStatsEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "emailNotificationInvoicesEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationRegistrationEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationShopEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationPatientHandOverEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationAppointmentEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationExerciseProgramEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationTrainingReminderEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationProgramStatsEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponseUserSettingsDto.prototype, "pushNotificationInvoicesEnabled", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseUserSettingsDto.prototype, "trainingReminderTime", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseUserSettingsDto.prototype, "defaultVideoCallDuration", void 0);
/**
 * Lightweight nested DTO for patient status within UserResponseDto.
 */
export class UserResponsePatientStatusDto {
    id;
    patientId;
    hasCompletedOnboarding;
    hasCompletedProfile;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponsePatientStatusDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponsePatientStatusDto.prototype, "patientId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponsePatientStatusDto.prototype, "hasCompletedOnboarding", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], UserResponsePatientStatusDto.prototype, "hasCompletedProfile", void 0);
/** Who declared a patient's consent in their context (#1843): the entry, as it reads. */
export class ConsentDeclarerDto {
    caregiverEntryId;
    name;
    relationship;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], ConsentDeclarerDto.prototype, "caregiverEntryId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], ConsentDeclarerDto.prototype, "name", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], ConsentDeclarerDto.prototype, "relationship", void 0);
/**
 * Lightweight nested DTO for consent declaration within UserResponseDto.
 */
export class UserResponseConsentDeclarationDto {
    id;
    consentVersion;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], UserResponseConsentDeclarationDto.prototype, "id", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], UserResponseConsentDeclarationDto.prototype, "consentVersion", void 0);
//# sourceMappingURL=user.response.dto.js.map